import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { completeConnect } from "../../../api/externalConnectionsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { toUiError, type UiError } from "../../../errors/uiError";
import StatusMessage from "../../../ui/feedback/StatusMessage";

type CallbackState = { readonly status: "working" } | { readonly status: "denied" } | { readonly status: "failed"; readonly error: UiError };

/**
 * `/connections/:provider/callback`: where the provider sends the browser back after the
 * person authorized DMW (ADR-0035). The page hands `state` and `code` to the server, signed
 * in, and returns to the page the person started from; when it can't, the shell's Back leads to
 * the profile (F87). The server, not this page, checks
 * that the `state` is theirs, unused and recent.
 */
export default function ConnectionCallbackPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { provider = "" } = useParams();
  const [query] = useSearchParams();
  const state = query.get("state");
  const code = query.get("code");
  const [outcome, setOutcome] = useState<CallbackState>(state === null || code === null ? { status: "denied" } : { status: "working" });
  const started = useRef(false);

  useEffect(() => {
    // Once only: a code works a single time, and a second request would be refused.
    if (started.current || state === null || code === null) return;
    started.current = true;
    completeConnect(provider, state, code).then(
      (returnTo) => { void navigate(returnTo, { replace: true }); },
      (error: unknown) => { setOutcome({ status: "failed", error: toUiError(error) }); },
    );
  }, [code, navigate, provider, state]);

  return (
    <div className="app-card w-full max-w-xl space-y-4 p-6 shadow-sm">
      <h1 className="app-title text-xl font-semibold">{t("profile.connections.callback.title")}</h1>
      {outcome.status === "working" ? <p role="status" className="app-help-text">{t("profile.connections.callback.working")}</p> : null}
      {outcome.status === "denied" ? <StatusMessage tone="warning">{t("profile.connections.callback.denied")}</StatusMessage> : null}
      {outcome.status === "failed" ? <ErrorNotice error={outcome.error} /> : null}
    </div>
  );
}
